import { useQuery } from '@apollo/client';
import {
  AddCircle,
  ContentCopy,
  Folder,
  MoreVert,
  type SvgIconComponent,
} from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDeleteShowFolder, useUpdateShowFolder } from '~/api/show-folders';
import { useCopyShow, useDeleteShow, useMoveShow } from '~/api/shows';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { MoveFolderDialog } from '~/components/dialogs/MoveFolderDialog';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  tableActionButtonProps,
  type GridActionCellProps,
} from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import { ShowMenuShowFolder, ShowMoveDocument, ShowRenameFolderDialog } from '.';
import { CreateViewWizard, useCreateViewWizard } from '../../components/create-view-wizard';
import type { ShowList__Show as Show, ShowList__ShowFolder } from '../../queries/queries.generated';
import { useShowFiltersParams } from '../context/ShowFiltersContext/searchParams';

export interface MenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  item: Show | ShowList__ShowFolder;
  selectedShow?: Show;
  onSelectedShow: (show: Show | undefined) => void;
  Icon?: SvgIconComponent;
}

export const Menu = ({
  buttonType = 'icon',
  item,
  selectedShow,
  onSelectedShow,
  Icon = MoreVert,
  ...cellProps
}: MenuProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const canManageViews = currentNetwork.canManageViews.value;

  const location = useLocation();
  const navigate = useNavigate();
  const link = useLink();
  const { tags } = useShowFiltersParams();

  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<typeof item>();
  const [copy] = useCopyShow();
  const viewWizard = useCreateViewWizard();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteShow] = useDeleteShow();
  const [deleteFolder] = useDeleteShowFolder();
  const [moveShow, moveShowInfo] = useMoveShow();
  const [updateShowFolder, updateShowFolderInfo] = useUpdateShowFolder();
  const [moveShowDialogOpen, setMoveShowDialogOpen] = useState(false);
  const [moveFolderDialogOpen, setMoveFolderDialogOpen] = useState(false);
  const [renameFolderDialogOpen, setRenameFolderDialogOpen] = useState(false);

  const { data: showMoveData } = useQuery(ShowMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveShowDialogOpen && !moveFolderDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      showMoveData?.network?.showFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ?? [],
    [showMoveData],
  );

  const setViewId = useCallback<(viewId: number, replace?: boolean) => void>(
    (id: number, replace = false) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('viewId', String(id));
      navigate(
        { pathname: `show/${selectedShow?.id}/design`, search: searchParams.toString() },
        { replace },
      );
    },
    [location.search, navigate, selectedShow?.id],
  );

  const onDuplicateShow = async () => {
    moreMenuProps.onClose();
    const show = (await copy({ variables: { showId: moreMenuProps.context?.id as number } })).data
      ?.copyShow?.show;
    navigate(
      link({
        pathname: `/shows/${show?.showFolderId}/show/${show?.id}/design`,
        search: location.search,
      }),
    );
  };

  const onMoveShow = () => {
    assert(moreMenuProps.context != null, 'media: no context');
    setMoveShowDialogOpen(true);
    moreMenuProps.onClose();
  };

  const itemActionProps = mergeGridActionProps(moreTableActionProps(item), cellProps);

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('pages.shows.menu.actions_tooltip')}
          {...tableActionButtonProps(itemActionProps)}
          size="small"
          color="secondary"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...itemActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        {item.__typename === 'Show' && (
          <MenuItem onClick={onMoveShow}>
            <ListItemIcon>
              <Folder />
            </ListItemIcon>
            <ListItemText>{t('pages.shows.menu.move_to_folder')}</ListItemText>
          </MenuItem>
        )}
        {item.__typename === 'Show' && (
          <MenuItem onClick={() => onDuplicateShow()}>
            <ListItemIcon>
              <ContentCopy />
            </ListItemIcon>
            <ListItemText>{t('pages.shows.menu.duplicate_show')}</ListItemText>
          </MenuItem>
        )}
        {item.__typename === 'Show' && canManageViews && (
          <MenuItem
            onClick={() => {
              onSelectedShow(item);
              viewWizard.open();
            }}
          >
            <ListItemIcon>
              <AddCircle />
            </ListItemIcon>
            <ListItemText>{t('pages.shows.menu.create_view')}</ListItemText>
          </MenuItem>
        )}

        {item.__typename === 'ShowFolder' && (
          <ShowMenuShowFolder
            item={item}
            onMove={() => {
              assert(moreMenuProps.context != null, 'media: no context');
              setMoveFolderDialogOpen(true);
              moreMenuProps.onClose();
            }}
            onRename={() => {
              setRenameFolderDialogOpen(true);
              moreMenuProps.onClose();
            }}
          />
        )}

        <Divider />

        <MoreDeleteAction
          {...moreActionProps}
          tooltip={item.canDestroy.reasons?.fullMessages.join(', ')}
          disabled={!item.canDestroy.value}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            if (item.__typename === 'ShowFolder') {
              await deleteFolder({
                variables: {
                  condition: { tags },
                  input: { showFolderId: moreMenuProps.context.id },
                },
              });
            } else {
              await deleteShow({
                refetchQueries: ['ShowList'],
                variables: { showIds: [moreMenuProps.context.id] },
              });
            }
            moreMenuProps.onClose();
          }}
        />
      </MuiMenu>

      <MoveToFolderDialog
        editableFolderIds={editableFolderIds}
        onSubmit={async (targetFolderId) => {
          await moveShow({
            refetchQueries: ['ShowList'],
            variables: {
              input: {
                showFolderId: targetFolderId,
                showIds: [item.id],
              },
            },
          });
        }}
        loading={moveShowInfo.loading}
        onClose={() => setMoveShowDialogOpen(false)}
        open={moveShowDialogOpen}
        i18nKey="pages.shows.list.dialogs.move_show.title"
      />
      <MoveFolderDialog
        editableFolderIds={editableFolderIds}
        folderId={item.id}
        onSubmit={async (targetFolderId) => {
          await updateShowFolder({
            refetchQueries: ['ShowList'],
            variables: {
              input: {
                showFolderId: item.id,
                patch: { parentId: targetFolderId },
              },
            },
          });
        }}
        loading={updateShowFolderInfo.loading}
        onClose={() => setMoveFolderDialogOpen(false)}
        open={moveFolderDialogOpen}
        i18nKey="common.dialogs.move_folder.title"
      />
      {item.__typename === 'ShowFolder' && (
        <ShowRenameFolderDialog
          showFolder={item}
          onClose={() => setRenameFolderDialogOpen(false)}
          open={renameFolderDialogOpen}
        />
      )}
      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.actions.cancel')}
        confirm={t('pages.shows.menu.delete_show_confirm')}
        deleteConfirm
        prompt={
          item.__typename === 'ShowFolder' ? (
            <Trans i18nKey="pages.shows.menu.delete_folder_prompt" components={{ 0: <b /> }} />
          ) : (
            <Trans i18nKey="pages.shows.menu.delete_show_prompt" components={{ 0: <b /> }} />
          )
        }
        title={
          item.__typename === 'ShowFolder'
            ? t('pages.shows.menu.delete_folder_title')
            : t('pages.shows.menu.delete_show_title')
        }
      />

      <CreateViewWizard {...viewWizard.props} show={selectedShow} setViewId={setViewId} />
    </>
  );
};
